import { configureStore } from "@reduxjs/toolkit";
import storage from "localforage";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";
import { combineReducers, UnknownAction } from "redux";
import { FLUSH, PAUSE, PERSIST, persistReducer, persistStore, PURGE, REGISTER, REHYDRATE } from "redux-persist";

import authReducer from "./reducers/auth.reducer";
import feedbackNotificationReducer from "./reducers/feedbackNotification.reducer";
import filtersReducer from "./reducers/filters.reducer";
import downloadJobsReducer from "./reducers/downloadJobs.reducer";
import { queries } from "./reducers/queries.reducer";

const AUTH_REDUCER_BLACKLIST_KEYS = ["loading", "success", "error", "currentAction"];
const DOWNLOAD_JOBS_REDUCER_BLACKLIST_KEYS: string[] = [];

const rootReducer = combineReducers({
	[authReducer.name]: persistReducer(
		{ key: authReducer.name, storage, blacklist: AUTH_REDUCER_BLACKLIST_KEYS },
		authReducer.reducer
	),
	[feedbackNotificationReducer.name]: feedbackNotificationReducer.reducer,
	[filtersReducer.name]: filtersReducer.reducer,
	[downloadJobsReducer.name]: persistReducer(
		{ key: downloadJobsReducer.name, storage, blacklist: DOWNLOAD_JOBS_REDUCER_BLACKLIST_KEYS },
		downloadJobsReducer.reducer
	),
	// RTK REDUCER
	[queries.reducerPath]: queries.reducer
});

type ReducerStateType = Parameters<typeof rootReducer>[0];

type ActionPayloadType = {
	status?: number;
	data?: { detail?: string };
};

export const store = configureStore({
	reducer: (state: ReducerStateType, action: UnknownAction) => {
		const actionPayload = action.payload ? (action.payload as ActionPayloadType) : undefined;

		const status = actionPayload?.status;
		const message = actionPayload?.data?.detail?.toLowerCase();

		const isInvalidToken = status === 401 && message === "invalid token.";
		const isDefaultPassword = status === 401 && message === "please change default password";
		const isResetPassword = status === 403 && message === "password has expired.";

		if (isInvalidToken || isDefaultPassword || isResetPassword) {
			const updatedState: ReducerStateType = state ? { ...state } : undefined;

			const authReducerName = authReducer.name;

			if (updatedState && updatedState[authReducerName]) {
				updatedState[authReducerName] = {
					...updatedState[authReducerName],
					loading: false,
					success: false,
					error: "",
					unauthorizedError: isInvalidToken,
					defaultPasswordError: isDefaultPassword,
					resetPasswordError: isResetPassword,
					user: {
						...updatedState[authReducerName].user,
						token: isInvalidToken ? "invalid" : updatedState[authReducerName].user.token
					}
				};
			}

			return rootReducer(updatedState, action);
		}

		return rootReducer(state, action);
	},

	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: {
				ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
			}
		}).concat(queries.middleware)
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
